import type {
  Coupon,
  CouponQuery,
  CouponStatus,
  CreateCoupon,
  UpdateCoupon,
} from '@monorepo/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/lib/toast'
import { couponService } from '../services/couponService'

// Helper to extract error messages
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { data?: { error?: { message?: string }; message?: string } }
      message?: string
    }
    return (
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'An error occurred'
    )
  }
  return 'An error occurred'
}

// ============================================================================
// QUERY KEYS (for cache management)
// ============================================================================

export const couponKeys = {
  all: ['coupons'] as const,
  lists: () => [...couponKeys.all, 'list'] as const,
  list: (filters?: CouponQuery) => [...couponKeys.lists(), filters] as const,
  details: () => [...couponKeys.all, 'detail'] as const,
  detail: (id: string) => [...couponKeys.details(), id] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get paginated list of coupons with filters
 */
export function useCoupons(filters?: CouponQuery) {
  return useQuery({
    queryKey: couponKeys.list(filters),
    queryFn: () => couponService.getCoupons(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  })
}

/**
 * Convenience hook that returns just the coupons array
 */
export function useCouponsList(filters?: CouponQuery) {
  const query = useCoupons(filters)
  const coupons = (query.data?.data || []) as Coupon[]

  return {
    ...query,
    coupons,
    pagination: query.data?.pagination,
  }
}

/**
 * Get single coupon by ID
 */
export function useCoupon(id: string) {
  return useQuery({
    queryKey: couponKeys.detail(id),
    queryFn: () => couponService.getCoupon(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new coupon
 */
export function useCreateCoupon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCoupon) => {
      return couponService.createCoupon(data)
    },
    onSuccess: newCoupon => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() })

      // Add to detail cache
      queryClient.setQueryData(couponKeys.detail(newCoupon.id), newCoupon)

      toast.success(`"${newCoupon.name}" has been created successfully.`)
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Update an existing coupon
 */
export function useUpdateCoupon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCoupon }) => {
      return await couponService.updateCoupon(id, data)
    },
    onSuccess: updatedCoupon => {
      // Update detail cache
      queryClient.setQueryData(
        couponKeys.detail(updatedCoupon.id),
        updatedCoupon
      )

      // Update list caches (handles both paginated and infinite queries)
      const matchingLists = queryClient.getQueriesData({
        queryKey: couponKeys.lists(),
      })
      for (const [key] of matchingLists) {
        queryClient.setQueryData(
          key,
          (existing: { data?: Coupon[] } | undefined) => {
            // Update coupon in paginated response
            if (existing?.data) {
              return {
                ...existing,
                data: existing.data.map((c: Coupon) =>
                  c.id === updatedCoupon.id ? updatedCoupon : c
                ),
              }
            }
            return existing
          }
        )
      }

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: couponKeys.detail(updatedCoupon.id),
      })

      toast.success(`"${updatedCoupon.name}" has been updated successfully.`)
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Delete a coupon
 */
export function useDeleteCoupon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => couponService.deleteCoupon(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: couponKeys.detail(id) })

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() })

      toast.success('The coupon has been deleted successfully.')
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Duplicate an existing coupon
 */
export function useDuplicateCoupon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data?: { name?: string; code?: string; status?: CouponStatus }
    }) => {
      return couponService.duplicateCoupon(id, data)
    },
    onSuccess: duplicatedCoupon => {
      // Add to detail cache
      queryClient.setQueryData(
        couponKeys.detail(duplicatedCoupon.id),
        duplicatedCoupon
      )

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() })

      toast.success(
        `Coupon "${duplicatedCoupon.name}" has been duplicated successfully.`
      )
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}
